'use client';

import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import styles from './MaterialTabs.module.scss';

export interface MaterialTabsProps {
  materialFacets: Record<string, number>;
}

interface MaterialCategory {
  id: string;
  values: string[];
  count: number;
  order: number;
}

interface CategoryDef {
  id: string;
  order: number;
}

// Keyed by the language-independent Algolia facet ID (the part after " | ")
const CATEGORY_BY_ID: Record<string, CategoryDef> = {
  CALFS: { id: 'leather', order: 1 },
  LIZ: { id: 'leather', order: 1 },
  ALLI: { id: 'alligator', order: 2 },
  CROC: { id: 'alligator', order: 2 },
  SHARK: { id: 'alligator', order: 2 },
  RUBBER: { id: 'rubber', order: 3 },
  ALLI_RUB: { id: 'rubber', order: 3 },
  CALFS_RUB: { id: 'rubber', order: 3 },
  POLYAMIDE: { id: 'fabric', order: 4 },
  ECONYL: { id: 'fabric', order: 4 },
  NATO: { id: 'fabric', order: 4 },
  STNSTL: { id: 'metal', order: 5 },
  TITAN: { id: 'metal', order: 5 },
  DLC_CTIT: { id: 'metal', order: 5 },
  '18KREDGLD': { id: 'metal', order: 5 },
  '18KROSGOL': { id: 'metal', order: 5 },
  STNSTL_18KREGLD: { id: 'metal', order: 5 },
  STNSTL_18KROGLD: { id: 'metal', order: 5 },
  SS_18KWHGG: { id: 'metal', order: 5 },
  'BETTER-GOLD': { id: 'metal', order: 5 },
};

function extractFacetId(facetKey: string): string | null {
  const pipeIndex = facetKey.lastIndexOf(' | ');
  return pipeIndex >= 0 ? facetKey.slice(pipeIndex + 3) : null;
}

function buildCategories(facets: Record<string, number>): MaterialCategory[] {
  const grouped = new Map<string, MaterialCategory>();

  for (const [facetKey, count] of Object.entries(facets)) {
    const facetId = extractFacetId(facetKey);
    const cat = facetId ? CATEGORY_BY_ID[facetId] : undefined;
    if (!cat) continue;

    const existing = grouped.get(cat.id);
    if (existing) {
      existing.values.push(facetKey);
      existing.count += count;
    } else {
      grouped.set(cat.id, {
        id: cat.id,
        values: [facetKey],
        count,
        order: cat.order,
      });
    }
  }

  const categories = Array.from(grouped.values());
  categories.sort((a, b) => a.order - b.order);
  return categories;
}

function getActiveCategoryId(
  materialParam: string[] | null,
  categories: MaterialCategory[]
): string | null {
  if (!materialParam || materialParam.length === 0) return null;
  return categories.find((cat) => cat.values.some((v) => materialParam.includes(v)))?.id ?? null;
}

export function MaterialTabs({ materialFacets }: Readonly<MaterialTabsProps>) {
  const t = useTranslations('plp');
  const { params, setFilter } = useSearchParams();

  const categories = useMemo(() => buildCategories(materialFacets), [materialFacets]);
  const activeCategoryId = getActiveCategoryId(params.material, categories);

  return (
    <div className={styles.tabs}>
      <h1 className={styles.heading}>{t('materialTabs.heading')}</h1>
      <nav aria-label={t('facets.material')}>
        <ul className={styles.tabList} role="tablist">
          <li role="presentation">
            <button
              role="tab"
              type="button"
              className={clsx(styles.tab, !activeCategoryId && styles.active)}
              aria-selected={!activeCategoryId}
              onClick={() => setFilter('material', null)}
            >
              {t('materialTabs.all')}
            </button>
          </li>
          {categories.map((cat) => {
            const isActive = cat.id === activeCategoryId;
            return (
              <li key={cat.id} role="presentation">
                <button
                  role="tab"
                  type="button"
                  className={clsx(styles.tab, isActive && styles.active)}
                  aria-selected={isActive}
                  onClick={() => setFilter('material', cat.values)}
                >
                  {t(`materialTabs.${cat.id}`)}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <hr className={styles.separator} />
    </div>
  );
}
